import { Fragment, type ReactNode } from 'react'

import { useAuthStore } from '@/stores/auth'

/**
 * Remounts its subtree whenever the signed-in identity is REPLACED, so no
 * component-local React state (an open dialog capturing a session title, a
 * half-rendered streaming buffer) carries the OUTGOING nurse's data into the
 * INCOMING one.
 *
 * On a shared bedside device a sibling-subdomain login can swap the active nurse
 * with NO page reload (reconcileIdentity → setAuth). The auth store's
 * tearDownUserScopedState() clears the MODULE-level surfaces (stores + caches);
 * this covers the component-local VIEW layer.
 *
 * WHICH transitions count is the store's decision, not this component's: it keys
 * on `identityEpoch`, which auth.ts bumps under the same predicate that gates
 * tearDownUserScopedState(). The two halves of one concept therefore cannot
 * drift. Deriving the rule here instead is what produced two separate
 * production bugs:
 *
 *   - Keying on the user id remounted on EVERY change, sign-in included. The
 *     pages that PERFORM a login live in this subtree and are mid-flight when
 *     setAuth fires, so signing in replaced the running instance with a fresh
 *     one, still on the callback URL, whose mount effect re-ran against a
 *     single-use credential the first instance had already consumed:
 *     /verify-email re-POSTed a spent token and got a 410 (#2151), and
 *     /auth/callback/<provider> re-read an `oauth_state` it had already removed
 *     from sessionStorage, compared it against null, and redirected a nurse
 *     whose login the server had just COMPLETED to /login?error=session_expired.
 *     Observed on mvp.tricorder.icu on 27 August 2026.
 *
 *   - Excluding the sign-in by holding a mirrored previous id and suppressing
 *     the key update made 'anon' mean both "never signed in" and "signed out
 *     again", so after one sign-in a LOGOUT computed the same key and did not
 *     remount at all — silently disabling the scrub this component exists for.
 *
 * A monotonic counter has neither failure mode: it cannot collide with a value
 * it already held, and only the store decides when it moves.
 *
 * Follow-up to #2147 (see that PR's tearDownUserScopedState "scope limit" note),
 * tracked as #2150.
 */
export function AuthIdentityBoundary({ children }: { children: ReactNode }) {
  const identityEpoch = useAuthStore((s) => s.identityEpoch)
  return <Fragment key={identityEpoch}>{children}</Fragment>
}
